<template>
  <InfoCard title="Session">
    <div v-if="!loading" class="info__grid scrollbar">
      <div v-if="currentSession" class="info__grid-field session-row">
        <div class="description">Session</div>
        <div class="value">
          <div style="display: flex; gap: 12px; align-items: center;">
            <div v-if="currentSession.id">
              <EncodedIdField
                :id="currentSession.id"
                :type="'trainer'"
                :hideLabel="true"
              />
            </div>
            <span v-if="currentSession.name" style="flex: 1;">
              {{ currentSession.name }}
            </span>
          </div>
        </div>
      </div>
      <div v-if="currentSession && currentSession.description" class="info__grid-field description-row">
        <div class="description">Description</div>
        <div
          v-tooltip="currentSession.description ? currentSession.description : 'Unknown'"
          class="value"
        >
          {{ currentSession.description ? currentSession.description : "Unknown" }}
        </div>
      </div>
      <div v-if="user" class="info__grid-field created-by-row">
        <div class="description">Created By</div>
        <div class="value">
          <UserIcon
            :user="user"
            :size="'32px'"
          />
        </div>
        <span class="ml-2">{{ user ? user.first_name + ' ' + user.last_name : '' }}</span>
      </div>
      <div v-if="currentSession && !currentSessionStatus.toLowerCase().includes('complete')" class="info__grid-field current-stage-row">
        <div class="description">Current Stage</div>
        <TaskStatusBars
          :task="currentSession.docker_task"
          :singleBar="true"
          class="progress-bar"
        />
      </div>
      <div v-if="currentSession" class="info__grid-field status-row">
        <div class="description">Status</div>
        <div>
          <span v-if="currentSessionStatus.toLowerCase().includes('complete')" class="status-tag _complete"><div class="status-icon">&#x2713;</div>Complete</span>
          <span v-else-if="currentSessionStatus.toLowerCase().includes('error')" class="status-tag _error"><div class="status-icon">&#x2716;</div>Error</span>
          <span v-else-if="currentSessionStatus.toLowerCase().includes('terminate')" class="status-tag _warning"><div class="status-icon">&#x21;</div>Terminated</span>
          <span v-else-if="currentSessionStatus" class="status-tag _running"><div class="running-icon" />
            {{ String(currentSessionStatus).charAt(0).toUpperCase() + String(currentSessionStatus).slice(1) }}
          </span>
        </div>
      </div>
      <div v-if="currentSession" class="info__grid-field model-type-row">
        <div class="description">Model Type</div>
        <div
          v-tooltip="currentSession.model ? currentSession.model : 'Unknown'"
          class="value"
        >
          {{ currentSession.model ? modelMap[currentSession.model] : 'Unknown' }}
        </div>
      </div>
      <div v-if="validateSessions && validateSessions.length > 0" class="info__grid-field validate-sessions-row">
        <div class="description">Validate Sessions</div>
        <div class="validate-sessions-list scrollbar">
          <div
            v-for="validateSession in validateSessions"
            :key="validateSession.id"
            v-tooltip="`View details for ${validateSession.docker_task.name}`"
            class="value hyperlink"
            @click="$emit('validate-session-click', validateSession)"
          >
            <span v-if="validateSession.docker_task">{{ `(v-${Number(validateSession.id).toString(16)})` }} {{ validateSession.docker_task.name }}</span>
          </div>
        </div>
      </div>
      <div v-if="currentSession" class="info__grid-field dataset-row">
        <div class="description">Dataset</div>
        <div class="value">
          <div style="display: flex; gap: 12px; align-items: center;">
            <div v-if="currentSession && currentSession.params.dataset_params && currentSession.params.dataset_params['dataset_id']">
              <EncodedIdField
                :id="currentSession.params.dataset_params['dataset_id']"
                :type="'dataset'"
                :hideLabel="true"
              />
            </div>
            <div
              v-if="currentSession && currentSession.params.trainer_params"
              v-tooltip="currentSession.params.trainer_params ? `Open gallery for dataset ${currentSession.params.trainer_params['dataset_name']}` : 'Unknown'"
              class="hyperlink"
              @click="$emit('to-gallery')"
              style="cursor: pointer;"
            >
              {{ currentSession.params.trainer_params ? currentSession.params.trainer_params['dataset_name'] : 'Unknown' }}
            </div>
            <span v-if="currentSession && currentSession.params.dataset_params && currentSession.params.dataset_params['tag_name']">
              Tag: {{ currentSession.params.dataset_params['tag_name'] }}
            </span>
          </div>
          <div v-if="currentSession && currentSession.params.dataset_params && currentSession.params.dataset_params['dataset_description']" style="margin-top: 6px; font-size: 0.85rem;">
            {{ currentSession.params.dataset_params['dataset_description'] }}
          </div>
        </div>
      </div>
      <div v-if="currentSession && currentSession.params.dataset_params && currentSession.params.dataset_params['annotation_set_id']" class="info__grid-field annotation-set-id-row">
        <div class="description">Annotation Set</div>
        <div class="value">
          <div style="display: flex; gap: 12px; align-items: center;">
            <div v-if="currentSession && currentSession.params.dataset_params && currentSession.params.dataset_params['annotation_set_id']">
              <EncodedIdField
                :id="currentSession.params.dataset_params['annotation_set_id']"
                :type="'annotation set'"
                :hideLabel="true"
              />
            </div>
          </div>
        </div>
      </div>
      <div v-if="currentSession && currentSession.params.dataset_params && currentSession.params.dataset_params['train_group_name']" class="info__grid-field train-group-row">
        <div class="description">Train Group</div>
        <div
          v-tooltip="currentSession.params.dataset_params ? currentSession.params.dataset_params['train_group_name'] : 'Unknown'"
          class="value"
        >
          {{ currentSession.params.dataset_params ? currentSession.params.dataset_params['train_group_name'] : 'Unknown' }}
        </div>
      </div>
      <div v-if="currentSession && currentSession.params.dataset_params && currentSession.params.dataset_params['val_group_name']" class="info__grid-field val-group-row">
        <div class="description">Val Group</div>
        <div
          v-tooltip="currentSession.params.dataset_params ? currentSession.params.dataset_params['val_group_name'] : 'Unknown'"
          class="value"
        >
          {{ currentSession.params.dataset_params ? currentSession.params.dataset_params['val_group_name'] : 'Unknown' }}
        </div>
      </div>
      <div v-if="currentSession" class="info__grid-field start-date-row">
        <div class="description">Start Date</div>
        <div class="value"><span v-if="currentSession.docker_task">{{ formatTime(currentSession.docker_task.date) }}</span></div>
      </div>
      <div v-if="currentSession" class="info__grid-field duration-row">
        <div class="description">Duration</div>
        <div class="value"><span v-if="currentSession.docker_task && getEndTime(currentSession.docker_task)" class="duration">{{ getEndTime(currentSession.docker_task) }}</span></div>
      </div>
    </div>
    <div v-else id="loading-visualization"><div class="lds-ring"><div /><div /><div /><div /></div></div>
  </InfoCard>
</template>

<script setup>
import EncodedIdField from '@/components/EncodedIdField.vue';
import UserIcon from '@/components/UserIcon.vue';
import TaskStatusBars from '@/components/TaskStatusBars.vue';
import InfoCard from '@/components/InfoCard.vue';

defineProps({
  currentSession: {
    type: Object,
    default: null,
  },
  user: {
    type: Object,
    default: null,
  },
  currentSessionStatus: {
    type: String,
    default: '',
  },
  validateSessions: {
    type: Array,
    default: null,
  },
  modelMap: {
    type: Object,
    default: () => ({}),
  },
  loading: {
    type: Boolean,
    default: false,
  },
});

defineEmits(['validate-session-click', 'to-gallery']);

const getEndTime = (docker_task) => {
  if (docker_task.end_date !== "0001-01-01T00:00:00Z" && docker_task.date !== "0001-01-01T00:00:00Z") {
    const msDiff = new Date(docker_task.end_date) - new Date(docker_task.date);
    const minutes = Math.floor(msDiff / (1000 * 60)) % 60;
    const hours = Math.floor(msDiff / (1000 * 60 * 60)) % 24;
    const days = Math.floor(msDiff / (1000 * 60 * 60 * 24));
    const result = [];
    if (days > 0) result.push(`${days} day${days > 1 ? 's' : ''}`);
    if (hours > 0) result.push(`${hours} hr${hours > 1 ? 's' : ''}`);
    if (minutes > 0) result.push(`${minutes} min${minutes > 1 ? 's' : ''}`);
    return result.join(" ") || "0 mins";
  } else if (docker_task.end_date === "0001-01-01T00:00:00Z" && docker_task.date !== "0001-01-01T00:00:00Z") {
    const msDiff = Date.now() - new Date(docker_task.date);
    const minutes = Math.floor(msDiff / (1000 * 60)) % 60;
    const hours = Math.floor(msDiff / (1000 * 60 * 60)) % 24;
    const days = Math.floor(msDiff / (1000 * 60 * 60 * 24));
    const result = [];
    if (days > 0) result.push(`${days} day${days > 1 ? 's' : ''}`);
    if (hours > 0) result.push(`${hours} hr${hours > 1 ? 's' : ''}`);
    if (minutes > 0) result.push(`${minutes} min${minutes > 1 ? 's' : ''}`);
    return result.join(" ") || "0 mins";
  } else {
    return null;
  }
};

const formatTime = (str) => `${new Date(str).toLocaleTimeString('default', {
  year: 'numeric', month: 'short', day: 'numeric', hour: 'numeric', minute: 'numeric', hour12: true,
})}`;
</script>

<style lang="scss" scoped>
.info__grid-field {
  display: flex;
  flex-direction: row;
  align-items: center;
  margin-top: 2px;
}

.validate-sessions-list {
  max-height: 200px;
  overflow-y: auto;
}

.info__grid {
  display: grid;
  overflow-y: auto;

  .description {
    font-size: 0.95rem;
    font-weight: 600;
    height: 32px;
    color: var(--body-text-color);
    width: 250px;
  }
  .value {
    font-size: 0.95rem;
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
  }

  .progress-bar {
    max-width: 400px;
    min-width: 100px;
  }
}

.status-tag {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100px;
  min-width: 100px;
  border-radius: 18px;
  padding: 4px 6px;
  line-height: 1;
  font-size: 0.625rem;
  font-weight: 700;
  white-space: nowrap;
  border: 2px solid;
  box-shadow: 0px 0px 1px rgb(0 0 0 / 25%), 0px 1px 4px rgb(0 0 0 / 25%);
  @include themify() {
    color: themed('color-black');
  }

  &._complete {
    @include themify() {
      color: themed('color-status-complete');
      background: themed('color-status-complete-background');
    }
  }
  &._running {
    @include themify() {
      color: themed('color-status-running');
      background: themed('color-status-running-background');
    }
  }
  &._error {
    @include themify() {
      color: themed('color-status-error');
      background: themed('color-status-error-background');
    }
  }

  &._warning {
    @include themify() {
      color: themed('color-status-warning');
      background: themed('color-status-warning-background');
    }
  }

  .running-icon {
    color: inherit;
    width: 4px;
    height: 4px;
    border: solid 4px;
    border-radius: 4px;
    margin-right: 4px;
  }

  .status-icon {
    color: inherit;
    margin-right: 4px;
  }
}

#loading-visualization {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
}

.lds-ring {
  display: inline-block;
  position: relative;
  width: 80px;
  height: 80px;
}

.lds-ring div {
  box-sizing: border-box;
  display: block;
  position: absolute;
  width: 64px;
  height: 64px;
  margin: 8px;
  border: 8px solid;
  border-radius: 50%;
  animation: lds-ring 1.2s cubic-bezier(0.5, 0, 0.5, 1) infinite;
  @include themify() {
    border-color: themed('color-primary-500') transparent transparent transparent;
  }
}

.lds-ring div:nth-child(1) { animation-delay: -0.45s; }
.lds-ring div:nth-child(2) { animation-delay: -0.3s; }
.lds-ring div:nth-child(3) { animation-delay: -0.15s; }

@keyframes lds-ring {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}
</style>
